import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { GalleryAction, useGalleryContext } from '..';
import { ActionsType } from '../types';
import { ActionButton } from './actionButton';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { createDeleteDocumentDialog } from 'components/core/Dialog/common/dialogTemplates';
import { getIssueAttachmentDisplayName } from 'services/fileService/common/getFileDisplayName';
import { CommonTranslationKeys, IssueTranslationKeys } from 'intl/keys';
import { formatTranslation, TranslationKey } from 'intl';

import {
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  RestoreFromTrash,
} from '@mui/icons-material';

interface RemoveButtonProps {
  idx: number;
  actions: ActionsType;
  isMobile: boolean;
  onMenuClose?: () => void;
}

export const RemoveButton: React.FC<RemoveButtonProps> = ({
  idx,
  actions,
  isMobile,
  onMenuClose,
}) => {
  const intl = useIntl();
  const createDialog = useDialog();
  const { preview, useDisplayedFiles, showActions } = useGalleryContext();
  const [file] = useDisplayedFiles((files) => files[idx]);
  const isArchivedFile = Boolean(file?.deleted);
  const isDocumentationPreview =
    showActions?.includes(GalleryAction.SHOW_ISSUES) &&
    showActions?.includes(GalleryAction.FILTERS);

  let labelKey: TranslationKey;
  if (isArchivedFile) {
    labelKey = CommonTranslationKeys.restore;
  } else if (isDocumentationPreview) {
    labelKey = CommonTranslationKeys.archive;
  } else {
    labelKey = IssueTranslationKeys.previewGalleryFileRemoveLabel;
  }
  const label = formatTranslation(intl, labelKey);

  const handleRemoveFile = useCallback(async (): Promise<void> => {
    if (preview) {
      return;
    }
    // Attachments should always confirm removal (same behavior as miniatures).
    if (!isDocumentationPreview && !isArchivedFile) {
      const titleForDialog = getIssueAttachmentDisplayName(file);
      try {
        await createDeleteDocumentDialog(createDialog, intl, titleForDialog);
      } catch {
        return;
      }
    }
    actions.removeFile(idx);
    if (onMenuClose) {
      onMenuClose();
    }
  }, [
    preview,
    isDocumentationPreview,
    isArchivedFile,
    file,
    createDialog,
    intl,
    actions,
    idx,
    onMenuClose,
  ]);

  return (
    <ActionButton
      isMobile={isMobile}
      onClick={handleRemoveFile}
      disabled={preview}
      tooltipTitle={label}
      label={label}
      icon={
        isArchivedFile ? (
          <RestoreFromTrash />
        ) : isDocumentationPreview ? (
          <DeleteIcon />
        ) : (
          <DeleteForeverIcon />
        )
      }
    />
  );
};
